import type { GameState } from '../game/GameState.js';
import type { MoveValidator } from '../game/MoveValidator.js';
import type { Color, Move } from '../game/types.js';

export interface MaterialCounts {
  readonly whiteMen: number;
  readonly whiteKings: number;
  readonly blackMen: number;
  readonly blackKings: number;
}

/**
 * AI-facing facade over `MoveValidator`. Delegates raw enumeration to the
 * validator (so variant rules stay in `game/`) and adds the ordering and
 * material-counting helpers that minimax and the heuristic evaluator both
 * need. Keeping this separate lets the search layer tune ordering without
 * touching variant rules.
 */
export class MoveEnumerator {
  constructor(private readonly validator: MoveValidator) {}

  /** All legal moves for the side to move. Captures-only when forced. */
  enumerate(state: GameState): Move[] {
    return this.validator.legalMoves(state);
  }

  /**
   * Legal moves ordered for alpha-beta. The validator already collapses to
   * captures-only when any exist; within that bucket we sort by chain length
   * (longer = more material swing) and put promotion-completing moves first.
   * The `pvMove` hint, when matches a candidate, is yielded first.
   */
  enumerateOrdered(state: GameState, pvMove: Move | null = null): Move[] {
    const moves = this.enumerate(state);
    if (moves.length <= 1) return moves;
    const scored = moves.map((m) => ({ move: m, score: orderingScore(m, pvMove) }));
    scored.sort((a, b) => b.score - a.score);
    return scored.map((s) => s.move);
  }

  static countMaterial(state: GameState): MaterialCounts {
    let whiteMen = 0;
    let whiteKings = 0;
    let blackMen = 0;
    let blackKings = 0;
    for (const { piece } of state.pieces()) {
      if (piece.color === 'white') {
        if (piece.kind === 'king') whiteKings += 1;
        else whiteMen += 1;
      } else {
        if (piece.kind === 'king') blackKings += 1;
        else blackMen += 1;
      }
    }
    return { whiteMen, whiteKings, blackMen, blackKings };
  }

  static pieceCountFor(material: MaterialCounts, color: Color): number {
    return color === 'white'
      ? material.whiteMen + material.whiteKings
      : material.blackMen + material.blackKings;
  }
}

function orderingScore(move: Move, pvMove: Move | null): number {
  if (pvMove && sameMove(move, pvMove)) return 1_000_000;
  // Captures dominate (validator filters when captures exist, but defend
  // against future refactors); within captures, longer chains first;
  // promotions add a bonus.
  const captureWeight = move.captures.length * 100;
  const promoteWeight = move.promoted ? 30 : 0;
  return captureWeight + promoteWeight;
}

function sameMove(a: Move, b: Move): boolean {
  if (a.from.row !== b.from.row || a.from.col !== b.from.col) return false;
  if (a.to.row !== b.to.row || a.to.col !== b.to.col) return false;
  if (a.captures.length !== b.captures.length) return false;
  for (let i = 0; i < a.captures.length; i += 1) {
    const ac = a.captures[i];
    const bc = b.captures[i];
    if (!ac || !bc) return false;
    if (ac.row !== bc.row || ac.col !== bc.col) return false;
  }
  return true;
}
